// AiAutomationPage — data-driven template for the per-industry AI Automation
// pages (e.g. /real-estate-ai-automation). Global only (no market prefix).
// Pulls automation content from aiAutomationData.js and the industry's base
// info (name, emoji, cover, related) from industryData.js.

import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  ArrowLeft,
  ChevronRight,
  Plus,
  Minus,
  Bot,
  MessageSquare,
  CalendarClock,
  RefreshCw,
  Star,
  Workflow,
  Map as MapIcon,
  PencilRuler,
  Wrench,
  Rocket,
  Activity,
  Sparkles,
  ShieldCheck,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import {
  getAiAutomationBySlug,
  AI_AUTOMATION_LIST,
} from "../data/aiAutomationData";
import { getIndustryBySlug } from "../data/industryData";

/* ─── Deployment process (shared across all industries) ──────── */
const DEPLOY_STEPS = [
  {
    n: "01",
    icon: MapIcon,
    title: "Map",
    text: "We audit your workflows and pinpoint the automations with the highest ROI — where AI saves the most time or captures the most revenue.",
  },
  {
    n: "02",
    icon: PencilRuler,
    title: "Design",
    text: "We design the conversation flows, guardrails and escalation rules so the AI sounds on-brand and never goes off-script.",
  },
  {
    n: "03",
    icon: Wrench,
    title: "Build & Integrate",
    text: "We build the agents and wire them into your CRM, calendar, booking and support tools so everything stays in sync.",
  },
  {
    n: "04",
    icon: Rocket,
    title: "Train & Launch",
    text: "We train on your verified data, test rigorously, and launch with human fallback so nothing slips through.",
  },
  {
    n: "05",
    icon: Activity,
    title: "Optimise",
    text: "We monitor performance, measure outcomes and continuously refine the automations as your business grows.",
  },
];

/* ─── Icons rotated across the automation cards ──────────────── */
const AUTOMATION_ICONS = [
  Bot,
  MessageSquare,
  CalendarClock,
  RefreshCw,
  Workflow,
  Star,
];

/* ─── FAQs shared across every industry (appended after specific) ─ */
const SHARED_FAQ = [
  {
    q: "How long until our AI automation is live?",
    a: "Most automations launch within 2–4 weeks. Simple agents can go live faster; deeper integrations with your CRM and tools take a little longer. We give you a clear timeline after the free audit.",
  },
  {
    q: "Is our data secure?",
    a: "Yes — we use encrypted, access-controlled data handling and scope everything to your industry's compliance requirements. The AI is trained only on your verified information, with strict guardrails.",
  },
  {
    q: "What does it cost?",
    a: "Typically a one-time build/setup fee plus a monthly retainer for hosting, monitoring and optimisation. We scope exact pricing to your workflows after a free automation audit — no commitment to see the plan.",
  },
];

/* ─── FAQ Accordion ──────────────────────────────────────────── */
function FAQAccordion({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <ul className="border-t border-zinc-200">
      {items.map((f, idx) => {
        const isOpen = open === idx;
        return (
          <li key={f.q} className="border-b border-zinc-200">
            <button
              onClick={() => setOpen(isOpen ? -1 : idx)}
              className="w-full py-6 flex items-center justify-between gap-6 text-left group"
              aria-expanded={isOpen}
            >
              <span className="font-display text-lg md:text-xl font-bold text-charcoal group-hover:text-electric transition-colors">
                {f.q}
              </span>
              <span
                className={`shrink-0 w-9 h-9 grid place-items-center rounded-xl border transition-colors ${
                  isOpen
                    ? "bg-electric border-electric text-white"
                    : "border-zinc-200 text-charcoal group-hover:border-electric"
                }`}
              >
                {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </span>
            </button>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="pb-6 pr-12"
              >
                <p className="text-zinc-600 leading-relaxed text-[15px]">{f.a}</p>
              </motion.div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function AiAutomationPage() {
  const { pathname } = useLocation();
  const aiSlug = pathname.replace(/^\//, "").split("/")[0];
  const data = getAiAutomationBySlug(aiSlug);
  const industry = data ? getIndustryBySlug(data.industrySlug) : null;

  if (!data || !industry) {
    return (
      <>
        <Navbar />
        <main className="pt-40 pb-40 text-center bg-white">
          <p className="text-overline text-electric mb-3">404</p>
          <h1 className="font-display text-4xl font-extrabold text-charcoal mb-6">
            AI automation page not found
          </h1>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3 font-semibold rounded-xl hover:bg-electric-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const pageUrl = `https://lumotis.com/${data.aiSlug}`;
  const faqItems = [...data.faq, ...SHARED_FAQ];

  // Related AI automation pages — derived from the industry's relatedSlugs.
  const related = (industry.relatedSlugs || [])
    .map((relIndustrySlug) =>
      AI_AUTOMATION_LIST.find((a) => a.industrySlug === relIndustrySlug)
    )
    .filter(Boolean)
    .map((a) => ({ ...a, industry: getIndustryBySlug(a.industrySlug) }))
    .filter((a) => a.industry)
    .slice(0, 3);

  return (
    <>
      <Helmet>
        <title>{data.metaTitle}</title>
        <meta name="description" content={data.metaDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={data.metaTitle} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        {industry.cover && <meta property="og:image" content={industry.cover} />}
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `${industry.name} AI Automation`,
            serviceType: "AI Automation",
            description: data.metaDescription,
            provider: {
              "@type": "Organization",
              name: "Lumotis",
              url: "https://lumotis.com",
            },
            areaServed: { "@type": "Country", name: "Global" },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
      </Helmet>

      <Navbar />

      <main data-testid={`ai-automation-page-${data.aiSlug}`}>
        {/* ══════════════════════════════════════ HERO */}
        <section className="relative bg-white pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden border-b border-zinc-100">
          <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
          <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/8 via-transparent to-transparent pointer-events-none" />
          <div
            aria-hidden
            className="absolute -left-32 bottom-0 w-[480px] h-[480px] glow-orb bg-electric/10 pointer-events-none"
          />

          <div className="container relative">
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 flex-wrap mb-8"
            >
              <Link to="/" className="hover:text-electric transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/#services" className="hover:text-electric transition-colors">AI Automation</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-charcoal">{industry.name}</span>
            </nav>

            <div className="grid lg:grid-cols-12 gap-12 items-center">
              {/* Left — copy */}
              <div className="lg:col-span-7">
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <div className="inline-flex items-center gap-2.5 border border-electric/25 bg-electric/8 rounded-full px-4 py-1.5">
                    <span className="text-xl leading-none" aria-hidden>{industry.emoji}</span>
                    <span className="text-overline text-electric">{data.heroEyebrow}</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 border border-electric/30 bg-electric/10 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest text-electric">
                    <Bot className="w-3.5 h-3.5" /> AI-Powered
                  </span>
                </div>

                <motion.h1
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="font-display text-4xl sm:text-5xl lg:text-[58px] font-extrabold leading-[1.05] tracking-tight text-balance text-charcoal"
                >
                  {data.heroHeadline}
                </motion.h1>

                {data.heroAccent && (
                  <p className="mt-4 font-display text-xl md:text-2xl font-bold leading-tight">
                    <span className="text-gradient">{data.heroAccent}</span>
                  </p>
                )}

                <p className="mt-6 text-lg text-zinc-500 leading-relaxed max-w-2xl">
                  {data.heroTagline}
                </p>

                <div className="mt-9 flex flex-wrap items-center gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 hover:-translate-y-0.5 transition-all shadow-lg shadow-electric/30"
                  >
                    Book a Free Automation Audit <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to={`/${industry.slug}`}
                    className="inline-flex items-center gap-2 bg-white border border-zinc-200 text-charcoal px-6 py-3.5 font-semibold rounded-xl hover:border-electric hover:text-electric transition-colors"
                  >
                    See {industry.shortName} Marketing <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-4 max-w-2xl">
                  {data.heroStats.map((s) => (
                    <div
                      key={s.label}
                      className="rounded-2xl border border-zinc-100 bg-white px-4 py-4 shadow-card"
                    >
                      <p className="font-display text-2xl md:text-3xl font-extrabold text-charcoal">
                        {s.value}
                      </p>
                      <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1 leading-snug">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — cover image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="lg:col-span-5"
              >
                <div className="relative">
                  <div className="absolute -inset-3 bg-electric/8 rounded-3xl -z-10" />
                  <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-zinc-100 bg-zinc-100 shadow-card-hover">
                    <img
                      src={industry.cover}
                      alt={`${industry.name} AI automation — Lumotis`}
                      className="w-full h-full object-cover"
                      loading="eager"
                      width="640"
                      height="480"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-electric text-white px-4 py-3 shadow-lg rounded-xl">
                    <p className="text-[10px] uppercase tracking-widest opacity-80">AI Automation</p>
                    <p className="font-display font-bold text-lg">{industry.shortName}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ WHY IT MATTERS */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">01 — Why it matters</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  The cost of doing this manually — and what AI changes.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                Three realities of {industry.shortName.toLowerCase()} that make automation pay for itself fast.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {data.insightStats.map((s, idx) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                  className="rounded-2xl border border-zinc-100 bg-white p-7 md:p-8 flex flex-col shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all"
                >
                  <p className="font-display text-4xl md:text-5xl font-extrabold text-charcoal leading-none mb-3">
                    {s.value}
                  </p>
                  <p className="font-display font-bold text-charcoal text-base leading-snug mb-2">
                    {s.label}
                  </p>
                  <p className="text-sm text-zinc-500 leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ AUTOMATIONS */}
        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-100">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">02 — What we automate</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  The AI automations we build for {industry.shortName.toLowerCase()}.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                Each one runs 24/7, integrates with your existing tools, and escalates to a human when it should.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.automations.map((a, idx) => {
                const Icon = AUTOMATION_ICONS[idx % AUTOMATION_ICONS.length];
                return (
                  <motion.div
                    key={a.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                    className="group rounded-2xl border border-zinc-100 bg-white p-7 md:p-8 shadow-card hover:shadow-card-hover hover:border-electric/30 hover:-translate-y-1 transition-all"
                  >
                    <div className="flex items-center justify-between mb-5">
                      <span className="w-11 h-11 grid place-items-center rounded-xl bg-electric/8 border border-electric/15">
                        <Icon className="w-5 h-5 text-electric" strokeWidth={2} />
                      </span>
                      <span className="font-display font-extrabold text-2xl text-electric/30">
                        0{idx + 1}
                      </span>
                    </div>
                    <h3 className="font-display text-xl font-bold text-charcoal group-hover:text-electric leading-snug mb-3 transition-colors">
                      {a.title}
                    </h3>
                    <p className="text-sm text-zinc-500 leading-relaxed">{a.body}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ CASE STUDY */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">03 — Proof</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  What AI automation delivered for a {industry.shortName.toLowerCase()} business.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                A real engagement — the kind of outcome automation produces when it's scoped right.
              </p>
            </div>

            <motion.article
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45 }}
              className="rounded-2xl bg-charcoal text-white p-8 md:p-12 shadow-glow-brand grid lg:grid-cols-12 gap-10 items-center"
            >
              <div className="lg:col-span-4">
                <span className="inline-block text-overline text-electric mb-5">
                  {data.caseStudy.badge}
                </span>
                <p className="font-display text-7xl md:text-8xl font-extrabold leading-none text-white tracking-tight">
                  {data.caseStudy.metric}
                </p>
                <p className="mt-2 text-zinc-400 text-sm uppercase tracking-widest">
                  {data.caseStudy.metricLabel}
                </p>
              </div>
              <div className="lg:col-span-8">
                <h3 className="font-display text-2xl md:text-3xl font-bold leading-snug">
                  {data.caseStudy.title}
                </h3>
                <p className="mt-4 text-zinc-300 leading-relaxed text-[15px]">
                  {data.caseStudy.body}
                </p>
                <Link
                  to="/case-studies"
                  className="mt-7 inline-flex items-center gap-2 text-electric font-semibold hover:gap-3 transition-all w-fit"
                >
                  See more case studies <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.article>
          </div>
        </section>

        {/* ══════════════════════════════════════ INTEGRATIONS / STACK (DARK) */}
        <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-[0.06] pointer-events-none" />
          <div
            aria-hidden
            className="absolute -right-32 top-0 w-[500px] h-[500px] glow-orb bg-electric/15 pointer-events-none"
          />
          <div className="container relative">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">04 — Integrations</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-balance leading-[1.05]">
                  Plugs into the tools you already use.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-400 leading-relaxed">
                Your AI agents read and write to your live systems, so every answer and booking is accurate.
              </p>
            </div>

            <div className="flex flex-wrap gap-2.5">
              {data.stack.map((t, idx) => (
                <motion.span
                  key={t}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: idx * 0.03 }}
                  className="inline-flex items-center gap-1.5 border border-white/15 bg-white/5 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-mono text-zinc-200 hover:border-electric hover:bg-electric/10 hover:text-white transition-colors"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-electric flex-shrink-0" />
                  {t}
                </motion.span>
              ))}
            </div>

            <div className="mt-10 inline-flex items-center gap-2 text-sm text-zinc-400">
              <ShieldCheck className="w-4 h-4 text-electric" />
              <span>Don't see your tool? We build custom integrations via API.</span>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ DEPLOYMENT PROCESS */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">05 — How we deploy</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  From workflow map to live automation — our 5-step process.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                Done-for-you. We build, integrate, train and maintain — you just watch the results.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
              {DEPLOY_STEPS.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: idx * 0.06 }}
                    className="rounded-2xl border border-zinc-100 bg-white p-6 md:p-7 flex flex-col shadow-card"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <span className="font-mono text-xs text-electric tracking-widest font-bold">
                        {step.n}
                      </span>
                      <span className="w-9 h-9 grid place-items-center rounded-xl bg-electric/8 border border-electric/15">
                        <Icon className="w-4 h-4 text-electric" strokeWidth={2} />
                      </span>
                    </div>
                    <h3 className="font-display text-lg font-bold text-charcoal mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-zinc-500 leading-relaxed">{step.text}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ FAQ */}
        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-100">
          <div className="container grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="text-overline text-electric mb-3">06 — FAQ</p>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                Questions about {industry.shortName} AI automation.
              </h2>
              <p className="mt-5 text-zinc-500 leading-relaxed">
                Have a different question? Book a free automation audit — a real strategist replies within 24h.
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 bg-electric text-white px-6 py-3 font-semibold rounded-xl hover:bg-electric-500 transition-colors shadow-lg shadow-electric/25"
              >
                Book a Free Audit <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-8">
              <FAQAccordion items={faqItems} />
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ RELATED AI AUTOMATION */}
        {related.length > 0 && (
          <section className="py-20 md:py-28 bg-white">
            <div className="container">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                <div>
                  <p className="text-overline text-electric mb-3">07 — More AI automation</p>
                  <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal leading-[1.05]">
                    AI automation for other industries.
                  </h2>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 border border-zinc-200 text-charcoal px-5 py-2.5 text-sm font-semibold rounded-xl hover:border-electric hover:text-electric transition-colors w-fit"
                >
                  Talk to us <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {related.map((r, idx) => (
                  <motion.div
                    key={r.aiSlug}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: idx * 0.06 }}
                  >
                    <Link
                      to={`/${r.aiSlug}`}
                      className="group rounded-2xl border border-zinc-100 bg-white p-7 md:p-8 flex flex-col h-full shadow-card hover:shadow-card-hover hover:border-electric/30 hover:-translate-y-1 transition-all"
                    >
                      <span className="text-3xl mb-5" aria-hidden>{r.industry.emoji}</span>
                      <h3 className="font-display text-xl font-bold text-charcoal group-hover:text-electric leading-snug transition-colors">
                        {r.industry.name} AI Automation
                      </h3>
                      <p className="mt-2 text-sm text-zinc-500 leading-relaxed flex-1">
                        {r.heroAccent}
                      </p>
                      <span className="mt-6 inline-flex items-center gap-1 text-xs font-bold text-zinc-400 group-hover:text-electric uppercase tracking-widest transition-colors">
                        Explore <ArrowUpRight className="w-3.5 h-3.5" />
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10 flex items-center gap-2 text-sm text-zinc-500">
                <Sparkles className="w-4 h-4 text-electric" />
                <span>
                  Also offering{" "}
                  <Link to={`/${industry.slug}`} className="text-electric font-semibold hover:underline">
                    {industry.shortName} marketing
                  </Link>{" "}
                  — pair automation with growth for compounding results.
                </span>
              </div>
            </div>
          </section>
        )}

        <CTABanner />
      </main>

      <Footer />
    </>
  );
}
